import styled from '@emotion/styled';
import { useMemo, useCallback, Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type { FreeTextbooksLessonQuery } from '__free_textbooks_generated__/FreeTextbooksLessonQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import MathContent from 'ms-components/math/MathContent';
import { ChapterLink } from 'ms-components/math/MathContent/ChapterLink';
import type { NodeRendererMap } from 'ms-components/math/MathContent/renderNode';
import { PageTimeRecorder } from 'ms-helpers/PageTimeTracker';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import { Base as CustomEmptyMessage } from 'ms-pages/Teacher/components/Empty';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import useTrackScrollDepthQuartiles from 'ms-pages/Textbooks/StudentTextbook/components/useTrackScrollDepthQuartiles';
import type { Quartile } from 'ms-pages/Textbooks/StudentTextbook/components/useTrackScrollDepthQuartiles';
import { InvestigationChapter } from 'ms-pages/Textbooks/components/InvestigationChapter';
import { SubtopicPkContext } from 'ms-pages/Textbooks/components/SubtopicDetailView/Lesson';
import { TheoryContentWrapper } from 'ms-pages/Textbooks/components/elements';
import _ from 'ms-utils/relay/extractNode';

import { FreeTextbookJsxLessonContainer } from './FreeTextbookJsxLessonContainer';

type Props = {
  subtopicId: string;
  scrollingElement: HTMLElement | null | undefined;
  nodeRenderers?: Partial<NodeRendererMap> | undefined;
};

export function FreeTextbooksLesson({
  subtopicId,
  scrollingElement,
  nodeRenderers,
}: Props) {
  const { trackStructEvent } = useSnowplow();
  useTrackScrollDepthQuartiles(
    scrollingElement,
    useCallback(
      (quartile: Quartile) => {
        if (quartile === '25') {
          trackStructEvent({
            category: 'free_textbook',
            action: 'scrolled_to_25_percent_depth_in_lesson',
            property: 'subtopicId',
            label: subtopicId,
          });
        }
        if (quartile === '50') {
          trackStructEvent({
            category: 'free_textbook',
            action: 'scrolled_to_50_percent_depth_in_lesson',
            property: 'subtopicId',
            label: subtopicId,
          });
        }

        if (quartile === '75') {
          trackStructEvent({
            category: 'free_textbook',
            action: 'scrolled_to_75_percent_depth_in_lesson',
            property: 'subtopicId',
            label: subtopicId,
          });
        }

        if (quartile === '100') {
          trackStructEvent({
            category: 'free_textbook',
            action: 'scrolled_to_100_percent_depth_in_lesson',
            property: 'subtopicId',
            label: subtopicId,
          });
        }
      },
      [subtopicId, trackStructEvent],
    ),
  );

  return (
    <LessonContainer subtopicId={subtopicId} nodeRenderers={nodeRenderers} />
  );
}

const ChapterContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

type LessonContainerProps = {
  subtopicId: string;
  nodeRenderers?: Partial<NodeRendererMap> | undefined;
};

function LessonContainer({ subtopicId, nodeRenderers }: LessonContainerProps) {
  return (
    <ErrorBoundaryWithRetry name="LessonContainer">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <LessonContainerInner
            subtopicId={subtopicId}
            nodeRenderers={nodeRenderers}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function LessonContainerInner({
  subtopicId,
  nodeRenderers,
  fetchKey,
}: LessonContainerProps & { fetchKey: string }) {
  const data = useLazyLoadQuery<FreeTextbooksLessonQuery>(
    graphql`
      query FreeTextbooksLessonQuery($subtopicId: ID!) {
        subtopic(id: $subtopicId) {
          # for optimization: While there are no rsl documents in production, this will always be empty
          ...FreeTextbookJsxLessonContainer_subtopic
          rslJsx {
            transpiledJsx
          }
          investigationJsx {
            transpiledJsx
          }
          hasInvestigations
          pk
          chapters(first: 1000) {
            edges {
              node {
                id
                content
                chapterType
                title
              }
            }
          }
        }
      }
    `,
    useMemo(() => ({ subtopicId }), [subtopicId]),
    {
      fetchKey,
      networkCacheConfig: {
        // @ts-expect-error TODO fix this once we upgrade relay and have sound types
        useCache: true,
        ttl: Infinity,
      },
    },
  );

  const { subtopic } = data;
  if (subtopic == null) return <>Subtopic not found</>;

  if (
    subtopic.rslJsx.transpiledJsx != null ||
    subtopic.investigationJsx.transpiledJsx != null
  )
    return <FreeTextbookJsxLessonContainer subtopicKey={subtopic} />;

  const filteredChapters = _(subtopic.chapters).filter(c =>
    ['LESSON', 'INVESTIGATION'].includes(c.chapterType),
  );

  return (
    <PageTimeRecorder componentName="Lesson" pageName="Textbooks">
      <TheoryContentWrapper>
        {filteredChapters.length === 0 ? (
          <CustomEmptyMessage label="There is no lesson content for this subtopic" />
        ) : (
          filteredChapters.map(c => (
            <ChapterContainer key={c.id}>
              <SubtopicPkContext.Provider value={subtopic.pk}>
                {c.chapterType === 'INVESTIGATION' ? (
                  <InvestigationChapter
                    chapterContent={c.content}
                    nodeRenderers={nodeRenderers}
                  />
                ) : (
                  <MathContent
                    content={c.content}
                    nodeRenderers={{
                      CHAPTER_LINK: ChapterLink,
                      ...nodeRenderers,
                    }}
                  />
                )}
              </SubtopicPkContext.Provider>
            </ChapterContainer>
          ))
        )}
      </TheoryContentWrapper>
    </PageTimeRecorder>
  );
}
